import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../common/context/AuthContext';
import { API_URL } from '../../api/config';

const FilterModal = ({ 
  visible, 
  onClose, 
  filters, 
  setFilters, 
  resetFilters 
}) => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [dateType, setDateType] = useState('start');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

  const confirmDate = () => {
    const selectedDate = new Date(year, month, 1);
    setFilters(prev => ({
      ...prev,
      [dateType === 'start' ? 'startDate' : 'endDate']: selectedDate
    }));
    setShowCustomDatePicker(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center sm:items-center">
      <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md max-h-[80%] overflow-auto">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Filters</h2>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <div className="grid grid-cols-2 gap-4">
              <button
                className="py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => {
                  setDateType('start');
                  setShowCustomDatePicker(true);
                }}
              >
                {filters.startDate ? 
                  `${months[filters.startDate.getMonth()]} ${filters.startDate.getFullYear()}` : 
                  'Start Date'}
              </button>
              <button
                className="py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => {
                  setDateType('end');
                  setShowCustomDatePicker(true);
                }}
              >
                {filters.endDate ? 
                  `${months[filters.endDate.getMonth()]} ${filters.endDate.getFullYear()}` : 
                  'End Date'}
              </button>
            </div>
          </div>
          
          <div className="flex space-x-4 mt-8">
            <button
              className="flex-1 py-3 px-4 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
              onClick={resetFilters}
            >
              Reset
            </button>
            <button
              className="flex-1 py-3 px-4 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700"
              onClick={onClose}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {showCustomDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-center mb-6">
              Select {dateType === 'start' ? 'Start' : 'End'} Date
            </h3>
            
            <div className="flex space-x-4">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Month</h4>
                <div className="h-48 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
                  {months.map((monthName, index) => (
                    <button
                      key={`month-${index}`}
                      className={`w-full py-2 px-3 mb-1 text-sm rounded-lg text-left ${
                        month === index 
                          ? 'bg-indigo-100 text-indigo-700 font-medium' 
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setMonth(index)}
                    >
                      {monthName}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-700 mb-2 text-center">Year</h4>
                <div className="h-48 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pr-2">
                  {years.map((yearNum) => (
                    <button
                      key={`year-${yearNum}`}
                      className={`w-full py-2 px-3 mb-1 text-sm rounded-lg text-left ${
                        year === yearNum 
                          ? 'bg-indigo-100 text-indigo-700 font-medium' 
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      onClick={() => setYear(yearNum)}
                    >
                      {yearNum}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <button
                className="flex-1 py-2 px-4 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
                onClick={() => setShowCustomDatePicker(false)}
              >
                Cancel
              </button>
              <button
                className="flex-1 py-2 px-4 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700"
                onClick={confirmDate}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BillHistory = () => {
  const { userToken } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    startDate: null,
    endDate: null,
    minAmount: '',
    maxAmount: ''
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchBills = useCallback(async (pageNum = page, isLoadMore = false) => {
    if (loading && isLoadMore) return;
    
    try {
      if (!isLoadMore) {
        setLoading(true);
        setBills([]);
      } else {
        setIsLoadingMore(true);
      }
      
      // Build query params object, excluding undefined/null values
      const params = {
        page: pageNum,
        limit: 10
      };

      // Only add filters if they have actual values
      if (filters.status) params.status = filters.status;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      if (filters.startDate) params.startDate = filters.startDate.toISOString();
      if (filters.endDate) params.endDate = filters.endDate.toISOString();
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.maxAmount) params.maxAmount = filters.maxAmount;

      const queryParams = new URLSearchParams(params).toString();

      const response = await fetch(`${API_URL}/bills?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message);

      if (isLoadMore) {
        setBills(prevBills => [...prevBills, ...data.bills]);
      } else {
        setBills(data.bills);
      }
      
      setTotalPages(data.totalPages);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [userToken, filters, page, loading]);

  useEffect(() => {
    setPage(1);
    fetchBills(1, false);
  }, [filters]);

  const resetFilters = () => {
    setFilters({
      status: '',
      paymentStatus: '',
      startDate: null,
      endDate: null,
      minAmount: '',
      maxAmount: ''
    });
  };

  const loadMore = () => {
    if (!loading && !isLoadingMore && page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchBills(nextPage, true);
    }
  };

  const getStatusColorClass = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-emerald-100 text-emerald-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Bill History</h1>
          <button
            className="px-4 py-2 rounded-full border border-indigo-500 text-indigo-600 hover:bg-indigo-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
            onClick={() => setShowFilters(true)}
          >
            <span className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
              </svg>
              Filters
            </span>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {loading && !isLoadingMore ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          </div>
        ) : bills.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900">No bills found</h3>
            <p className="mt-1 text-gray-500">Try adjusting your filters or create a new bill.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {bills.map((bill) => (
              <Link
                key={bill.id || bill._id}
                to={`/billdetails/${bill.id || bill._id}`}
                className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow transform hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="p-5">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Bill #{bill.billNumber}</h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColorClass(bill.status)}`}>
                      {bill.status}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-xl font-bold text-emerald-600">₹{bill.totalAmount.toFixed(2)}</p>
                      {bill.discount > 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          ({(bill.discount / bill.totalAmount * 100).toFixed(1)}% off: -₹{bill.discount.toFixed(2)})
                        </p>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {new Date(bill.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500">Items</p>
                        <p className="text-sm">{bill.items.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Table</p>
                        <p className="text-sm">{bill.tableNumber}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Payment</p>
                        <p className="text-sm">{bill.paymentStatus}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Method</p>
                        <p className="text-sm">{bill.paymentMethod || 'Not specified'}</p>
                      </div>
                    </div>
                    {bill.names && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-500">Names</p>
                        <p className="text-sm italic truncate">{bill.names}</p>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        
        {!loading && bills.length > 0 && page < totalPages && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMore}
              className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : 'Load More'}
            </button>
          </div>
        )}
      </div>

      <FilterModal 
        visible={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
      />
    </div>
  );
};

export default BillHistory;
